import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileSearch } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Mono,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@lagoon/ui";

import { api, type AuditEvent } from "../api.js";

/**
 * The audit trail.
 *
 * Read-only by construction: there is no endpoint that edits or deletes a row,
 * and the database rejects the attempt regardless of who makes it. What this
 * screen adds is the ability to answer "who did that, and why" without a psql
 * session.
 */

/**
 * Radix refuses an empty string as a `SelectItem` value, because an empty
 * value is how it represents "nothing chosen". "Any action" is a real choice,
 * so it gets a sentinel of its own and is mapped back to an absent filter.
 */
const ANY_ACTION = "*";

/**
 * The filters arrive as props rather than being read off `window.location`
 * here.
 *
 * The router owns the hash, including its query, and hands this screen the
 * parts it understands. That is what makes `#/audit?siteId=<a>` followed by
 * `#/audit?siteId=<b>` show the second site's rows: `App` keys this component
 * on the filters, so the paging state resets with them. Reading the location
 * in a `useState` initialiser, as this did, meant the first site's id survived
 * every later navigation (LGN-125, audit H2).
 */
export function AuditScreen({
  siteId = "",
  initialActor = "",
}: {
  siteId?: string;
  initialActor?: string;
}): React.JSX.Element {
  const [action, setAction] = useState(ANY_ACTION);
  const [actor, setActor] = useState(initialActor);
  const [pages, setPages] = useState<number[]>([]);

  const actions = useQuery({ queryKey: ["audit-actions"], queryFn: () => api.auditActions() });

  const before = pages.at(-1);
  const filtered = action === ANY_ACTION ? "" : action;
  const events = useQuery({
    queryKey: ["audit", filtered, actor, siteId, before],
    queryFn: () =>
      api.audit({
        ...(filtered ? { action: filtered } : {}),
        ...(actor ? { actor } : {}),
        ...(siteId ? { siteId } : {}),
        ...(before === undefined ? {} : { before }),
        limit: 50,
      }),
  });

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        eyebrow="Record"
        title="Audit trail"
        description="Every mutation this platform has made, with the reason its author gave."
      />

      {siteId !== "" && (
        <Alert tone="info">
          <AlertTitle>Filtered to one site</AlertTitle>
          <AlertDescription>
            <a href="#/audit" className="lg:text-primary lg:hover:underline">
              Show everything
            </a>
          </AlertDescription>
        </Alert>
      )}

      <div className="lg:flex lg:flex-wrap lg:gap-4">
        <FormField label="Action" className="lg:min-w-64">
          <Select
            value={action}
            onValueChange={(next) => {
              setAction(next);
              setPages([]);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_ACTION}>any</SelectItem>
              {actions.data?.actions.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField label="Actor" className="lg:min-w-64">
          <Input
            type="text"
            value={actor}
            placeholder="email address"
            onChange={(event) => {
              setActor(event.target.value);
              setPages([]);
            }}
          />
        </FormField>
      </div>

      {events.error && (
        <Text size="sm" tone="danger" role="alert">
          {events.error.message}
        </Text>
      )}

      {events.data &&
        (events.data.events.length === 0 ? (
          <EmptyState
            icon={<FileSearch />}
            title="Nothing recorded for that filter"
            description="Widen the action or clear the actor and try again."
            glow={false}
          />
        ) : (
          <>
            <ol className="lg:flex lg:flex-col lg:gap-3">
              {events.data.events.map((event) => (
                <AuditRow key={event.id} event={event} />
              ))}
            </ol>
            <div className="lg:flex lg:gap-3">
              {pages.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPages((p) => p.slice(0, -1));
                  }}
                >
                  Back
                </Button>
              )}
              {events.data.nextBefore !== null && events.data.events.length === 50 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const next = events.data.nextBefore;
                    if (next !== null) setPages((p) => [...p, next]);
                  }}
                >
                  Older
                </Button>
              )}
            </div>
          </>
        ))}
    </div>
  );
}

function AuditRow({ event }: { event: AuditEvent }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const hasDetail = event.beforeState !== null || event.afterState !== null;

  return (
    <li>
      <Card padded={false} interactive={false} className="lg:px-5 lg:py-4">
        <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-3">
          <Mono as="time" className="lg:text-caption lg:text-subtle">
            {new Date(event.occurredAt).toLocaleString()}
          </Mono>
          <Badge tone="neutral">{event.action}</Badge>
          <Text size="caption" tone="muted" as="span">
            {event.actorEmail ?? `${event.actorType} (unnamed)`}
          </Text>
          {hasDetail && (
            <Button
              variant="ghost"
              size="sm"
              className="lg:ml-auto"
              aria-expanded={open}
              onClick={() => {
                setOpen(!open);
              }}
            >
              {open ? "Hide" : "Detail"}
            </Button>
          )}
        </div>

        {event.reason !== null && (
          <Text size="sm" className="lg:mt-2 lg:max-w-none">
            <strong className="lg:text-strong">Reason:</strong> {event.reason}
          </Text>
        )}

        {event.siteId !== null && (
          <Text size="caption" tone="subtle" className="lg:mt-2">
            <a href={`#/site/${event.siteId}`} className="lg:text-primary lg:hover:underline">
              site {event.siteId.slice(0, 8)}
            </a>
            {event.ip !== null && ` · from ${event.ip}`}
          </Text>
        )}

        {open && (
          <pre className="lg:mt-3 lg:overflow-x-auto lg:rounded-sm lg:bg-brand-bg2 lg:p-3 lg:font-mono lg:text-caption lg:text-muted-foreground">
            {JSON.stringify({ before: event.beforeState, after: event.afterState }, null, 2)}
          </pre>
        )}
      </Card>
    </li>
  );
}
